// ─── Connection ────────────────────────────────────────────────────

/** Possible states of the realtime connection. */
export type RealtimeConnectionState = 'connected' | 'connecting' | 'disconnected' | 'error' | 'reconnecting';

/** Configuration for the realtime service. */
export interface RealtimeConfig {
  /** Direct WebSocket client URL (for local development). */
  clientUrl?: string;
  /** Heartbeat interval in ms (default: 30000). */
  heartbeatIntervalMs?: number;
  /** Maximum number of reconnection attempts (default: 5). */
  maxReconnectAttempts?: number;
  /** Backend negotiate endpoint URL (recommended for production). */
  negotiateUrl?: string;
  /** Delay between reconnection attempts in ms (default: 5000). */
  reconnectIntervalMs?: number;
}

/** Feature flags to enable/disable realtime per resource. */
export type RealtimeFeatureFlags = Record<string, boolean>;

// ─── Notifications ─────────────────────────────────────────────────

/** Discriminant for realtime notification envelopes. */
export type RealtimeNotificationType = 'resource' | 'user';

/** Operation that triggered the notification. */
export type RealtimeResourceOperation = 'created' | 'deleted' | 'updated';

/**
 * Well-known `event` values for `UserRealtimeNotification` — avoids string drift between the
 * producer (back-messenger) and consumers (e.g. the frontoffice realtime listener). Add a new key
 * here for every new user-scoped event; no other wiring is required for it to be usable.
 */
export const REALTIME_USER_EVENTS = { ETA_COMPUTED: 'eta-computed', IMPORT_COMPLETED: 'import-completed' } as const;
export type RealtimeUserEvent = (typeof REALTIME_USER_EVENTS)[keyof typeof REALTIME_USER_EVENTS];

export interface RealtimeNotificationBase {
  /** Correlation ID for tracing. */
  correlationId?: string;
  /** Tenant ID for multi-tenant isolation. */
  tenantId?: string;
  /** Discriminant for the notification envelope. */
  type: RealtimeNotificationType;
}

/** Notification envelope for resource-level CRUD operations. */
export interface ResourceRealtimeNotification<T = unknown> extends RealtimeNotificationBase {
  /** Operation that was performed on the resource. */
  operation: RealtimeResourceOperation;
  /** The resource payload. */
  payload: T;
  /** The resource name (e.g. "orders"). */
  resource: string;
  type: 'resource';
}

/**
 * Notification envelope for a single-user-targeted event — generic by design (an `event` string
 * discriminates the payload shape) so a new user-scoped event only needs a new `REALTIME_USER_EVENTS`
 * key, never a new envelope type.
 */
export interface UserRealtimeNotification<T = unknown> extends RealtimeNotificationBase {
  /** Well-known event name, e.g. `REALTIME_USER_EVENTS.IMPORT_COMPLETED`. */
  event: string;
  /** The event payload. */
  payload: T;
  type: 'user';
  /** Id of the single user this notification targets. */
  userId: string;
}

/** Union of all realtime notification shapes (extensible via discriminant). */
export type PharosRealtimeNotification<T = unknown> = ResourceRealtimeNotification<T> | UserRealtimeNotification<T>;

/** Handler function for a realtime notification subscription. */
export type RealtimeNotificationHandler<T = unknown> = (
  notification: PharosRealtimeNotification<T>,
) => void | Promise<void>;

// ─── Subscriptions ─────────────────────────────────────────────────

/** Options for subscribing to realtime notifications. */
export interface RealtimeSubscriptionOptions<TData = unknown> {
  /** Whether this subscription is active (default: true). */
  enabled?: boolean;
  /** Event(s) to filter on for `type: 'user'` notifications. Required for a subscription to ever match a user notification. */
  event?: string | string[];
  /** Callback when a matching notification is received. */
  handler: RealtimeNotificationHandler<TData>;
  /** Operation(s) to filter on. Omit to match all operations. */
  operation?: string | string[];
  /** Resource(s) to filter on. Omit to match all resources. */
  resource?: string | string[];
}

/** State returned by the useRealtimeSubscription hook. */
export interface RealtimeSubscriptionState {
  /** Whether the subscription is currently active. */
  isSubscribed: boolean;
  /** Unique subscription ID (null if not subscribed). */
  subscriptionId: string | null;
}

// ─── Provider ──────────────────────────────────────────────────────

/** State exposed by the RealtimeProvider context. */
export interface RealtimeContextState {
  /** Manually trigger a connection (if not auto-connected). */
  connect: () => Promise<void>;
  /** Manually disconnect. */
  disconnect: () => void;
  /** Last connection error, if any. */
  error: Error | null;
  /** Whether the client is currently connected. */
  isConnected: boolean;
  /** Whether realtime is enabled via configuration. */
  isEnabled: boolean;
  /** Current connection state. */
  state: RealtimeConnectionState;
}

// ─── Presence ──────────────────────────────────────────────────────

/** Presence update payload sent to POST /realtime/presence. */
export interface RealtimePresencePayload {
  /** The page identifier (e.g. 'orders-list'). */
  page: string;
  /** Whether the user is entering or leaving the page. */
  state: 'enter' | 'leave';
}

// ─── Server-side routing config ────────────────────────────────────

/** Config entry for server-side realtime routing (one entry per resource). */
export interface RealtimeRoutingConfigEntry {
  /** The page the user must be on to receive broadcasts. */
  requiredPage: string;
  /** The permission the user must hold to receive broadcasts. */
  requiredPermission: string;
  /**
   * When `true` and the event carries none of `scopeAnchors` (e.g. a tenant-level KPI), restricts
   * recipients to connections holding a tenant-level scope instead of broadcasting to every
   * connection in the tenant regardless of scope. Defaults to `false`.
   */
  restrictToTenantScopeWhenUnanchored?: boolean;
  /** Scope anchor fields on the event identifiers used for scope matching. */
  scopeAnchors: string[];
  /** The Service Bus topics that trigger broadcasts for this resource. */
  topics: string[];
}

/** Full routing config map, keyed by resource name. */
export type RealtimeRoutingConfig = Record<string, RealtimeRoutingConfigEntry>;

// ─── Negotiate response ────────────────────────────────────────────

/** Response returned by POST /realtime/negotiate. */
export interface RealtimeNegotiateResponse {
  /** Web PubSub groups the client should join. */
  groups: string[];
  /** Client access token. */
  token: string;
  /** WebSocket client URL. */
  url: string;
}
